import React from 'react';

import {DashboardPage} from 'app/components/general/DashboardPage';
import {useCurrentUserState} from 'app/current-user/api';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {useLocalStateFilters} from 'app/hooks';
import {
  convertKnowledgebaseLocationParamsToFetchParams,
  useKnowledgeBases,
} from 'app/kbs/api';
import {KB_DASHBOARD_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt, ngt} from 'app/utils/i18n';

import {CompactKBListItem} from './CompactKBListItem';
import {CreateKBButton} from './CreateKBButton';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {KBFetchParamsType, KBLocationParamsType} from 'app/kbs/types';
import type {WithRouterProps} from 'react-router';
import type {KnowledgebaseSerializedType} from 'sweet-types';

export function KnowledgebasesDashboard(routerProps: WithRouterProps) {
  const {is_super_manager} = useCurrentUserState().data!;

  return (
    <DashboardPage<
      KnowledgebaseSerializedType,
      KBLocationParamsType,
      KBFetchParamsType
    >
      {...routerProps}
      title={gt('Knowledge Bases')}
      actionArea={
        is_super_manager ? (
          <CreateKBButton className="btn btn-sm btn-primary">
            <i className="fa fa-fw fa-plus-circle"></i>{' '}
            <span className="d-none d-md-inline">
              {gt('Create Knowledge base')}
            </span>
          </CreateKBButton>
        ) : null
      }
      useFiltersHook={useLocalStateFilters<
        KBFetchParamsType,
        KBLocationParamsType
      >(convertKnowledgebaseLocationParamsToFetchParams)}
      useDataHook={useKnowledgeBases}
      renderItem={(kb) => (
        <CompactKBListItem
          kb={kb}
          isSuperManager={is_super_manager}
          key={kb.id}
        />
      )}
      messages={{
        counter: (count) =>
          ngt('%s knowledge base', '%s knowledge bases', count),
        notFound: (props, NoResultsMessage) => (
          <NoResultsMessage
            icon="far fa-book-open"
            head={gt('No Knowledge Bases were created yet')}
          >
            {is_super_manager ? (
              <div className="px-5">
                <p className="lead mx-md-5 px-sm-5">
                  {gt(
                    'Let your customers, suppliers and others access some of your procedures and policies in a beautiful knowledge base.'
                  )}
                </p>
                <CreateKBButton className="btn btn-primary btn-lg">
                  {gt('Create Knowledge Base')}
                </CreateKBButton>
              </div>
            ) : null}
          </NoResultsMessage>
        ),
      }}
      subscribeToPresence={{
        contentType: 'knowledgebase',
        objectsIds: (kbs) => kbs.map((kb) => kb.id),
      }}
    />
  );
}

const kbCt = getContentType('knowledgebase');

export const KBsDashboardBreadcrumb = (props: BreadcrumbProps) => (
  <BreadcrumbItemStructure
    {...props}
    icon={kbCt.icon}
    text={gt('Knowledge Bases')}
    link={KB_DASHBOARD_URL}
  />
);
